import { useExperience } from './experience';

/**
 * Short UI sounds synthesised with Web Audio (no files to download). Played only when the person has sound on, at
 * their volume, and never more than one at a time. Every tone goes through one soft low-pass filter and a master gain,
 * so the set sounds like one family: warm, short, never shrill.
 */
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let last = 0;
let lastSoft = 0;

type Tone = { f: number; at: number; dur: number; type?: OscillatorType; gain?: number; to?: number; detune?: number };

function out(): { c: AudioContext; m: GainNode } {
  ctx ??= new AudioContext();
  if (!master) {
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 5200;
    lp.Q.value = 0.4;
    master = ctx.createGain();
    master.connect(lp).connect(ctx.destination);
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return { c: ctx, m: master };
}

/** Soft sounds (taps, opening a dialog) never hold back a result sound that follows them. */
function play(tones: Tone[], always = false, soft = false) {
  const { sound: on, volume } = useExperience.getState();
  if (!always && !on) return;
  const now = performance.now();
  if (soft ? now - lastSoft < 60 || now - last < 120 : now - last < 120) return;
  if (soft) lastSoft = now;
  else last = now;
  try {
    const { c, m } = out();
    // 0.6 is the original loudness; warnings that must be heard never go below a quiet floor.
    m.gain.value = Math.max(always ? 0.35 : 0, Math.min(1, volume ?? 0.6)) / 0.6;
    const t0 = c.currentTime + 0.005;
    for (const t of tones) {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = t.type ?? 'sine';
      o.frequency.setValueAtTime(t.f, t0 + t.at);
      if (t.detune) o.detune.value = t.detune;
      if (t.to) o.frequency.exponentialRampToValueAtTime(t.to, t0 + t.at + t.dur);
      g.gain.setValueAtTime(0, t0 + t.at);
      g.gain.linearRampToValueAtTime(t.gain ?? 0.07, t0 + t.at + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + t.at + t.dur);
      o.connect(g).connect(m);
      o.start(t0 + t.at);
      o.stop(t0 + t.at + t.dur + 0.02);
    }
  } catch {
    /* audio not available */
  }
}

/** A note with a quiet octave shimmer (a bell-like, friendly timbre). */
const bell = (f: number, at: number, dur: number, gain = 0.06): Tone[] => [
  { f, at, dur, gain },
  { f: f * 2, at, dur: dur * 0.6, gain: gain * 0.22, detune: 4 },
];

export const sound = {
  /** Approved / completed / saved. */
  success: () => play([...bell(880, 0, 0.18), ...bell(1318.5, 0.09, 0.3)]),
  /** Rejected / error. */
  error: () => play([{ f: 392, at: 0, dur: 0.16, type: 'triangle', gain: 0.09 }, { f: 311, at: 0.14, dur: 0.24, type: 'triangle', gain: 0.09 }]),
  /** A warning the person must notice (lines merged, a rule applied) — plays even with sounds off (owner, 2026-10-26). */
  ding: () => play([...bell(987.77, 0, 0.14, 0.08), ...bell(739.99, 0.11, 0.28, 0.08)], true),
  /** A scan or tap accepted. */
  tick: () => play([{ f: 1760, at: 0, dur: 0.045, type: 'triangle', gain: 0.03 }], false, true),
  /** Overdue / shortage alert. */
  alert: () => play([{ f: 660, at: 0, dur: 0.12, type: 'triangle', gain: 0.06 }, { f: 660, at: 0.18, dur: 0.14, type: 'triangle', gain: 0.06 }]),
  /** A button pressed (save, submit, approve): a soft, short tap. */
  press: () => play([{ f: 520, at: 0, dur: 0.06, type: 'triangle', gain: 0.035, to: 380 }], false, true),
  /** A dialog or panel opens. */
  open: () => play([{ f: 620, at: 0, dur: 0.11, gain: 0.035, to: 930 }], false, true),
  /** A dialog, panel or menu closes. */
  close: () => play([{ f: 880, at: 0, dur: 0.09, gain: 0.025, to: 560 }], false, true),
  /** Moving to another screen. */
  nav: () => play([{ f: 1240, at: 0, dur: 0.05, gain: 0.018, to: 1480 }], false, true),
  /** A switch turned on / off. */
  toggle: (on: boolean) => play([{ f: on ? 740 : 560, at: 0, dur: 0.07, type: 'triangle', gain: 0.03, to: on ? 990 : 420 }], false, true),
  /** Something new arrived (notification, inbox item). */
  notify: () => play([...bell(1046.5, 0, 0.12, 0.05), ...bell(1567.98, 0.08, 0.22, 0.045)]),
  /** Approved / posted: a warm rising chord under the "done" stamp. */
  approve: () => play([...bell(659.25, 0, 0.2, 0.05), ...bell(830.61, 0.06, 0.22, 0.045), ...bell(987.77, 0.12, 0.34, 0.05)]),
  /** Milestone (released, completed, handed over). */
  milestone: () => play([...bell(523.25, 0, 0.16), ...bell(659.25, 0.1, 0.16), ...bell(783.99, 0.2, 0.16), ...bell(1046.5, 0.3, 0.45)]),
};
